import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  linkedSignal,
  OnDestroy,
  signal,
  TemplateRef,
  viewChild
} from '@angular/core';
import { DatePipe } from '@angular/common';
import {
  AbilityService,
  DealStatus,
  DealStatusLabels,
  LocalStorageManagerService,
  NgbModalService,
  rxResourceFixed,
  SkeletonComponent
} from '@zayan-erp/shared';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbModal, NgbModalRef, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { DsmApsService, DsmRoomReplacementReviewExecution, DsmRoomReplacementReviewRoom } from '@zayan-erp/dsm';
import { DealOptionsService } from '../../services/deal-options.service';
import { AddAddendumModalComponent } from '../deal-detail/options/add-addendum-modal/add-addendum-modal.component';
import { DealAddendumPreviewComponent } from './deal-addendums/deal-addendum-preview/deal-addendum-preview.component';
import { DealAddendumOptionsComponent } from './deal-addendums/deal-addendum-options/deal-addendum-options.component';
import { DealAddendumDetailsComponent } from './deal-addendums/deal-addendum-details/deal-addendum-details.component';
import { DealAddendumOptionDetailsComponent } from './deal-addendums/deal-addendum-options/deal-addendum-option-details/deal-addendum-option-details.component';
import { DealOption } from '../../models/deal-options-view.models';
import { SalesApsService } from '../../services/sales-aps.service';
import { of } from 'rxjs';

type DealOptionWithExpandableView = DealOption & {
  isExpanded: boolean;
};
@Component({
  selector: 'deal-addendums-view',
  imports: [
    DealAddendumPreviewComponent,
    DealAddendumOptionsComponent,
    DealAddendumDetailsComponent,
    DealAddendumOptionDetailsComponent,
    NgbTooltipModule,
    DatePipe,
    SkeletonComponent
  ],
  providers: [],
  templateUrl: './deal-addendums-view.component.html',
  styleUrl: './deal-addendums-view.component.scss'
})
export class DealAddendumsViewComponent implements OnDestroy {
  private readonly dealOptionsService = inject(DealOptionsService);
  private readonly salesApsService = inject(SalesApsService);
  private readonly dsmApsService = inject(DsmApsService);
  private readonly localStorageManager = inject(LocalStorageManagerService);
  private readonly abilityService = inject(AbilityService);
  ability = this.abilityService.ability;
  /** Single source of truth for this component's write gate. */
  canWriteDeals = computed(() => this.ability.can('write-sales-deals', 'deals'));
  readonly selectAllPrintCheckbox = viewChild<ElementRef<HTMLInputElement>>('selectAllPrint');
  readonly modalConfig = inject(NgbModalService);
  readonly modal = inject(NgbModal);
  readonly destroyRef = inject(DestroyRef);

  readonly roomReplacementDisclosureModal = viewChild<TemplateRef<unknown>>('roomReplacementDisclosureModal');
  #roomReplacementDisclosureModalRef: NgbModalRef | null = null;

  roomNames(rooms: DsmRoomReplacementReviewRoom[]): string {
    return (
      rooms
        .map(room => room.locationName)
        .filter(Boolean)
        .join(', ') || '—'
    );
  }

  /** Deal from route context (when loaded by router). */
  effectiveDeal = computed(() => this.salesApsService.currentDeal());
  effectiveDealId = computed(() => this.effectiveDeal()?.id ?? '');
  effectiveLotId = computed(() => this.effectiveDeal()?.lot.id ?? '');
  effectiveModelId = computed(() => this.effectiveDeal()?.model?.id ?? '');
  effectiveElevationId = computed(() => this.effectiveDeal()?.elevation?.id ?? '');
  isUnderContract = computed(() => this.effectiveDeal()?.dealStatus?.name === 'Under Contract');
  /**
   * A cancelled deal's addendums are read-only. Its clean-up has already run, so anything added or
   * approved here would stay active on the lot for the next buyer, who never sees it on their own page.
   */
  isDealCancelled = computed(() => this.effectiveDeal()?.dealStatus?.name === DealStatusLabels[DealStatus.Cancelled]);
  effectiveTargetDate = computed(() => this.effectiveDeal()?.contract?.targetClosingDate ?? null);

  reviewResource = rxResourceFixed({
    params: () => ({ dealId: this.effectiveDealId() }),
    defaultValue: null,
    stream: ({ params }) => {
      if (!params.dealId) return of(null);
      return this.dsmApsService.getRoomReplacementReview(params.dealId, false);
    }
  });

  allExecutions = computed<DsmRoomReplacementReviewExecution[]>(() => this.reviewResource.value()?.replacements ?? []);
  hasExecutedRule = computed(() => this.allExecutions().length > 0);

  executedRuleTags = computed<{ executionId: string; label: string }[]>(() =>
    this.allExecutions().map(execution => ({
      executionId: execution.executionId,
      label: execution.triggerProductDescription || execution.triggerProductCode || 'A room replacement rule'
    }))
  );

  acknowledgedExecutionIds = signal<Set<string>>(
    this.localStorageManager.getAcknowledgedRoomReplacementExecutionIds('sales')
  );

  pendingDisclosureExecutions = computed<DsmRoomReplacementReviewExecution[]>(() =>
    this.allExecutions().filter(execution => !this.acknowledgedExecutionIds().has(execution.executionId))
  );

  currentDisclosure = computed<DsmRoomReplacementReviewExecution | null>(
    () => this.pendingDisclosureExecutions()[0] ?? null
  );

  currentDisclosureLabel = computed<string | null>(() => {
    const execution = this.currentDisclosure();
    if (!execution) return null;
    return execution.triggerProductDescription || execution.triggerProductCode || 'A room replacement rule';
  });

  private readonly openDisclosureModal = effect(() => {
    const execution = this.currentDisclosure();
    if (!execution || this.#roomReplacementDisclosureModalRef) return;
    const template = this.roomReplacementDisclosureModal();
    if (!template) return;
    this.#roomReplacementDisclosureModalRef = this.modal.open(template, this.modalConfig.confirmationModal);
  });

  acknowledgeCurrentDisclosure(): void {
    const execution = this.currentDisclosure();
    if (!execution) return;
    this.localStorageManager.acknowledgeRoomReplacementExecution(execution.executionId, 'sales');
    this.acknowledgedExecutionIds.update(ids => new Set(ids).add(execution.executionId));
    this.#roomReplacementDisclosureModalRef?.close();
    this.#roomReplacementDisclosureModalRef = null;
  }

  ngOnDestroy(): void {
    this.#roomReplacementDisclosureModalRef?.close();
  }

  selectedAddendumId = signal<string | null>(null);
  selectedProductId = signal<string | null>(null);
  /** IDs selected for PDF print; empty means "print all" on the print action. */
  selectedAddendumIdsForPrint = signal<string[]>([]);

  dealOptionsHistory: DealOptionWithExpandableView[] = [];

  dealsOptionsRxResource = rxResourceFixed({
    params: () => ({ dealId: this.effectiveDealId(), lotId: this.effectiveLotId() }),
    defaultValue: [],
    stream: ({ params }) => {
      if (!params.lotId || !params.dealId) return of([]);
      return this.dealOptionsService.getAddendums(params.lotId, params.dealId, false);
    }
  });

  isDealOptionsLoading = computed(() => this.dealsOptionsRxResource.isLoading());
  dealsOptions = linkedSignal<DealOptionWithExpandableView[]>(() => {
    const options = this.dealsOptionsRxResource.value() ?? [];
    return options.map(option => {
      const isPreviouslyExpanded = this.dealOptionsHistory.find(history => history.id === option.id)?.isExpanded;
      const hasNoProducts = option.products.length === 0;
      if (isPreviouslyExpanded === false) {
        return {
          ...option,
          isExpanded: false
        } as DealOptionWithExpandableView;
      }

      return {
        ...option,
        isExpanded: isPreviouslyExpanded || hasNoProducts
      } as DealOptionWithExpandableView;
    });
  });

  onDealsOptionsSet = effect(() => {
    this.dealOptionsHistory = this.dealsOptions();
  });

  /** Drop print selections that no longer exist after reload. */
  private readonly syncPrintSelectionWithOptions = effect(() => {
    const options = this.dealsOptions();
    const validIds = new Set(options.map(o => o.id));
    this.selectedAddendumIdsForPrint.update(sel => {
      const next = sel.filter(id => validIds.has(id));
      return next.length === sel.length && next.every((id, i) => id === sel[i]) ? sel : next;
    });
  });

  isAllAddendumsSelectedForPrint = computed(() => {
    const opts = this.dealsOptions();
    const n = opts.length;
    if (n === 0) return false;
    const sel = this.selectedAddendumIdsForPrint();
    return sel.length === n && opts.every(o => sel.includes(o.id));
  });

  printAddendumsButtonLabel = computed(() => {
    const count = this.selectedAddendumIdsForPrint().length;
    if (count === 0) return 'Print all addendums';
    return `Print ${count} addendums`;
  });

  private readonly syncSelectAllIndeterminate = effect(() => {
    const el = this.selectAllPrintCheckbox()?.nativeElement;
    if (!el) return;
    const total = this.dealsOptions().length;
    const selected = this.selectedAddendumIdsForPrint().length;
    el.indeterminate = total > 0 && selected > 0 && selected < total;
  });

  selectedAddendum = computed(
    () => this.dealsOptions().find(addendum => addendum.id === this.selectedAddendumId()) ?? null
  );

  showAddendumDetails = computed(() => this.selectedAddendum() !== null && this.selectedProductId() === null);

  showOptionDetails = computed(() => this.selectedAddendum() !== null && this.selectedProductId() !== null);

  onOptionChanges(): void {
    this.dealsOptionsRxResource.reload();
    this.reviewResource.reload();
  }

  openAddOptionModal(): void {
    if (!this.canWriteDeals()) return;
    const modalRef = this.modal.open(AddAddendumModalComponent, this.modalConfig.centerModal);
    const lotId = this.effectiveLotId();
    const dealId = this.effectiveDealId();

    if (lotId) modalRef.componentInstance.setLotId(lotId);
    if (dealId) modalRef.componentInstance.setDealId(dealId);

    // const subscription = modalRef.componentInstance.saved.subscribe((savedData: AddendumCreatedEvent) => {
    const subscription = modalRef.componentInstance.saved.subscribe((savedData: any) => {
      // this.newlyCreatedBuilderReferenceId.set(savedData.builderReferenceId);
      this.dealsOptionsRxResource.reload();
      subscription.unsubscribe();
    });

    modalRef.closed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      subscription.unsubscribe();
    });
  }

  // make sure this is a string
  onAddendumSelect(optionId: string): void {
    this.selectedAddendumId.set(optionId);
    this.selectedProductId.set(null);
  }

  selectedKitProductId: string | null = null;
  onAddendumProductSelect(addendumId: string, productId: string, isKitLinkedProduct: boolean = false): void {
    this.selectedAddendumId.set(addendumId);
    this.selectedProductId.set(productId);
    if (isKitLinkedProduct) {
      this.selectedKitProductId = productId;
    } else {
      this.selectedKitProductId = null;
    }
  }

  isAddendumSelectedForPrint(id: string): boolean {
    return this.selectedAddendumIdsForPrint().includes(id);
  }

  onAddendumPrintToggle(addendumId: string, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.selectedAddendumIdsForPrint.update(ids => {
      if (checked) {
        return ids.includes(addendumId) ? ids : [...ids, addendumId];
      }
      return ids.filter(i => i !== addendumId);
    });
  }

  onSelectAllPrintChange(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    const dealIds = this.dealsOptions().map(o => o.id);
    if (checked) {
      this.selectedAddendumIdsForPrint.set([...dealIds]);
    } else {
      this.selectedAddendumIdsForPrint.set([]);
    }
  }

  printAddendums(): void {
    const dealId = this.effectiveDealId();
    if (!dealId || this.dealsOptions().length === 0) return;
    const selected = this.selectedAddendumIdsForPrint();
    const idsForRequest = selected.length > 0 ? selected : null;
    const lotNo = this.effectiveDeal()?.lot?.lotNo;
    this.dealOptionsService
      .downloadAddendumsPdf(dealId, idsForRequest, `${lotNo}-Addendum-${+new Date()}.pdf`)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }
}
